import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Loader2,
  Mail,
  Trash2,
  KeyRound,
  ShieldOff,
  Power,
  PowerOff,
  Copy,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABEL = { admin: "Administrador", gestor: "Gestor(a)", consultant: "Consultor(a)" };

const AdminUsers = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "consultant" });
  const [inviteResult, setInviteResult] = useState(null); // {email, temp_password, email_sent}
  const [confirm, setConfirm] = useState(null); // {type, user}
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/admin/users", form);
      if (data.email_sent) {
        toast.success(`Convite enviado para ${data.user.email}.`);
        setInviteResult(null);
      } else {
        // Email failed — display temp password to admin so they can share manually
        setInviteResult({
          email: data.user.email,
          temp_password: data.temp_password,
          email_sent: false,
        });
        toast.warning("Usuário criado, mas o e-mail falhou. Compartilhe a senha temporária manualmente.");
      }
      setForm({ name: "", email: "", role: "consultant" });
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const doAction = async () => {
    if (!confirm) return;
    setWorking(true);
    const { type, user } = confirm;
    try {
      if (type === "delete") {
        await api.delete(`/admin/users/${user.id}`);
        toast.success("Usuário excluído.");
      } else if (type === "deactivate") {
        await api.patch(`/admin/users/${user.id}`, { is_active: false });
        toast.success("Usuário desativado.");
      } else if (type === "activate") {
        await api.patch(`/admin/users/${user.id}`, { is_active: true });
        toast.success("Usuário reativado.");
      } else if (type === "reset_mfa") {
        await api.post(`/admin/users/${user.id}/reset-mfa`);
        toast.success("MFA resetado. Usuário fará novo enrollment no próximo login.");
      } else if (type === "reset_password") {
        const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
        if (data.email_sent) {
          toast.success("Senha redefinida e enviada por e-mail.");
        } else {
          setInviteResult({
            email: user.email,
            temp_password: data.temp_password,
            email_sent: false,
          });
          toast.warning("Senha redefinida — e-mail falhou. Compartilhe manualmente.");
        }
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copiado."),
      () => toast.error("Falha ao copiar.")
    );
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Painel administrativo
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Users className="text-[#B9262B]" /> Usuários
          </h1>
          <p className="text-sm text-[#9aa3ad] mt-1">
            Convide consultores e administradores. Cada novo usuário recebe uma senha temporária
            por e-mail e configura MFA no primeiro acesso.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          data-testid="invite-user-btn"
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
        >
          <UserPlus size={16} className="mr-2" /> Convidar usuário
        </Button>
      </div>

      <div className="rounded-xl border border-[#3A3F47] bg-[#1C1F26] overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <Loader2 className="animate-spin inline-block mr-2" /> Carregando...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[#6B8DB3] bg-[#0e1015]">
              <tr>
                <th className="text-left p-4">Nome</th>
                <th className="text-left p-4">E-mail</th>
                <th className="text-left p-4">Perfil</th>
                <th className="text-left p-4">MFA</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4 hidden lg:table-cell">Convidado por</th>
                <th className="text-right p-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="border-t border-[#3A3F47] hover:bg-[#252932]/40"
                  >
                    <td className="p-4 font-medium text-white">
                      {u.name}
                      {isSelf && (
                        <span className="ml-2 text-[10px] text-[#22B673] uppercase tracking-wider">
                          Você
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#9aa3ad]">{u.email}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                          u.role === "admin"
                            ? "bg-[#B9262B]/15 text-[#B9262B] border border-[#B9262B]/40"
                            : u.role === "gestor"
                            ? "bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/40"
                            : "bg-[#6B8DB3]/15 text-[#6B8DB3] border border-[#6B8DB3]/40"
                        }`}
                      >
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="p-4">
                      {u.mfa_enabled ? (
                        <span className="inline-flex items-center gap-1 text-[#22B673] text-xs">
                          <CheckCircle2 size={14} /> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#6B8DB3] text-xs">
                          <XCircle size={14} /> Pendente
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 text-[#22B673] text-xs">
                          <Power size={14} /> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#B9262B] text-xs">
                          <PowerOff size={14} /> Inativo
                        </span>
                      )}
                      {u.must_change_password && (
                        <div className="text-[10px] text-[#F5A623] mt-1">
                          Aguardando 1º acesso
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-[#6B8DB3] text-xs hidden lg:table-cell">
                      {u.invited_by || "—"}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {!isSelf && (
                          <button
                            onClick={() => setConfirm({ type: "reset_password", user: u })}
                            data-testid={`reset-pwd-${u.id}`}
                            title="Redefinir senha"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#9aa3ad] hover:text-[#F5A623] hover:bg-[#F5A623]/10 border border-[#3A3F47]"
                          >
                            <Mail size={13} />
                          </button>
                        )}
                        {!isSelf && u.mfa_enabled && (
                          <button
                            onClick={() => setConfirm({ type: "reset_mfa", user: u })}
                            data-testid={`reset-mfa-${u.id}`}
                            title="Resetar MFA"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#9aa3ad] hover:text-[#6B8DB3] hover:bg-[#6B8DB3]/10 border border-[#3A3F47]"
                          >
                            <ShieldOff size={13} />
                          </button>
                        )}
                        {!isSelf && (
                          u.is_active ? (
                            <button
                              onClick={() => setConfirm({ type: "deactivate", user: u })}
                              data-testid={`deactivate-${u.id}`}
                              title="Desativar usuário"
                              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#9aa3ad] hover:text-[#B9262B] hover:bg-[#B9262B]/10 border border-[#3A3F47]"
                            >
                              <PowerOff size={13} />
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirm({ type: "activate", user: u })}
                              data-testid={`activate-${u.id}`}
                              title="Reativar usuário"
                              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#9aa3ad] hover:text-[#22B673] hover:bg-[#22B673]/10 border border-[#3A3F47]"
                            >
                              <Power size={13} />
                            </button>
                          )
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => setConfirm({ type: "delete", user: u })}
                            data-testid={`delete-${u.id}`}
                            title="Excluir usuário"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#9aa3ad] hover:text-[#B9262B] hover:bg-[#B9262B]/10 border border-[#3A3F47]"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(v) => !inviting && setInviteOpen(v)}>
        <DialogContent
          data-testid="invite-dialog"
          className="bg-[#1C1F26] border-[#3A3F47] text-white sm:max-w-[480px]"
        >
          <form onSubmit={sendInvite}>
            <DialogHeader>
              <DialogTitle className="text-white">Convidar novo usuário</DialogTitle>
              <DialogDescription className="text-[#9aa3ad]">
                Uma senha temporária será gerada e enviada por e-mail. No primeiro acesso o
                usuário definirá uma nova senha e configurará MFA.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-4">
              <div>
                <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">Nome</Label>
                <Input
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-name"
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1 h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">E-mail</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-email"
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1 h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger
                    data-testid="invite-role"
                    className="bg-[#0e1015] border-[#3A3F47] text-white mt-1 h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
                    <SelectItem value="consultant">Consultor(a) — somente Diagnósticos</SelectItem>
                    <SelectItem value="gestor">Gestor(a) — Diagnósticos + visualização de Sessões/Perguntas/Produtos/Listas</SelectItem>
                    <SelectItem value="admin">Administrador — acesso total</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setInviteOpen(false)}
                disabled={inviting}
                className="text-[#9aa3ad] hover:text-white hover:bg-[#0e1015]"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={inviting}
                data-testid="send-invite-btn"
                className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
              >
                {inviting ? <Loader2 className="animate-spin" size={16} /> : "Enviar convite"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Temp password fallback dialog (when email fails) */}
      <Dialog open={!!inviteResult} onOpenChange={(v) => !v && setInviteResult(null)}>
        <DialogContent className="bg-[#1C1F26] border-[#F5A623]/40 text-white sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[#F5A623]">Senha temporária — compartilhe manualmente</DialogTitle>
            <DialogDescription className="text-[#9aa3ad]">
              O e-mail falhou. Repasse a senha abaixo para o usuário por outro canal seguro.
            </DialogDescription>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-3 my-4">
              <div>
                <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">E-mail</Label>
                <div className="font-mono text-sm bg-[#0e1015] border border-[#3A3F47] rounded p-2 mt-1 break-all">
                  {inviteResult.email}
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B8DB3] uppercase tracking-wider">Senha temporária</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 font-mono text-base bg-[#0e1015] border border-[#3A3F47] rounded p-2 break-all">
                    {inviteResult.temp_password}
                  </code>
                  <button
                    onClick={() => copy(inviteResult.temp_password)}
                    className="p-2 rounded text-[#9aa3ad] hover:text-white hover:bg-[#0e1015] border border-[#3A3F47]"
                    title="Copiar"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setInviteResult(null)}
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm destructive action */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !working && setConfirm(null)}>
        <AlertDialogContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirm?.type === "delete" && "Excluir usuário?"}
              {confirm?.type === "deactivate" && "Desativar usuário?"}
              {confirm?.type === "activate" && "Reativar usuário?"}
              {confirm?.type === "reset_mfa" && "Resetar MFA?"}
              {confirm?.type === "reset_password" && "Redefinir senha?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9aa3ad]">
              {confirm?.type === "delete" && (
                <>Esta ação é permanente. O acesso de <strong className="text-white">{confirm?.user?.email}</strong> será removido.</>
              )}
              {confirm?.type === "deactivate" && (
                <>O usuário <strong className="text-white">{confirm?.user?.email}</strong> não poderá mais acessar o painel até ser reativado.</>
              )}
              {confirm?.type === "activate" && (
                <>Restaurar o acesso de <strong className="text-white">{confirm?.user?.email}</strong>.</>
              )}
              {confirm?.type === "reset_mfa" && (
                <>O usuário <strong className="text-white">{confirm?.user?.email}</strong> precisará escanear um novo QR code no próximo login.</>
              )}
              {confirm?.type === "reset_password" && (
                <>Será gerada uma nova senha temporária para <strong className="text-white">{confirm?.user?.email}</strong> e enviada por e-mail. A senha atual será invalidada.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={working}
              className="bg-[#0e1015] border-[#3A3F47] text-white hover:bg-[#252932] hover:text-white"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doAction}
              disabled={working}
              data-testid="confirm-user-action-btn"
              className={
                confirm?.type === "delete" || confirm?.type === "deactivate"
                  ? "bg-[#B9262B] hover:bg-[#9a1e22] text-white"
                  : "bg-[#22B673] hover:bg-[#1a9b60] text-white"
              }
            >
              {working ? <Loader2 className="animate-spin" size={16} /> : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
